import React from "react";
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import "./base-card.css";

/**
 * Standard header row for a card / section.
 *
 * The old pattern floated (or absolutely positioned) the section's Edit button
 * against the end of its own *content* column, so the button's X position moved
 * with the amount of content in the section - stacked cards ended up with their
 * Edit buttons on three different vertical lines, separated from the content by
 * a large dead gap, and two sections even pulled the button back up with a magic
 * `marginTop: -255`.
 *
 * Here the action lives in the header row instead: title left, actions pushed
 * right by `margin-left:auto`, and the row carries the card's own 24px side
 * padding. Every card that uses this header therefore lines its Edit button up
 * on the same right edge automatically, whatever its content is - no fixed
 * pixel coordinates involved.
 *
 * Props:
 *  - title       section title (string or node)
 *  - subtitle    optional muted line under the title
 *  - onEdit      click handler; renders the standard Edit button when provided
 *  - canEdit     gate for that button (defaults to true when onEdit is given)
 *  - editText    button label (default "Edit")
 *  - actions     extra nodes rendered before the Edit button
 */
const BaseCardHeader = ({
  title,
  subtitle,
  onEdit,
  canEdit = true,
  editText = "Edit",
  editProps = {},
  actions,
  className = "",
  children,
}) => {
  const showEdit = !!onEdit && canEdit;

  return (
    <div className={ `klok-card-header${className ? ` ${className}` : ""}` }>
      <div className="klok-card-header__titles">
        { typeof title === "string" || typeof title === "number"
          ? <h5 className="klok-card-header__title">{ title }</h5>
          : title }
        { subtitle ? <span className="klok-card-header__subtitle">{ subtitle }</span> : null }
      </div>
      <div className="klok-card-header__actions">
        { actions }
        { children }
        { showEdit ? (
          <Button
            type="primary"
            className="klok-card-header__edit"
            onClick={ onEdit }
            { ...editProps }
          >
            <EditOutlined aria-hidden="true" />
            <span>{ editText }</span>
          </Button>
        ) : null }
      </div>
    </div>
  );
};

BaseCardHeader.displayName = "BaseCardHeader";

export default BaseCardHeader;
